import { useState } from 'react';
import { LangCode, getTranslation } from '../../../components/feature/LanguageSelector';

export interface FilterState {
  operationType: 'all' | 'venta' | 'alquiler' | 'parcelas' | 'construccion';
  propertyTypes: string[];
  location: string;
  priceMin: string;
  priceMax: string;
  bedrooms: string;
  bathrooms: string;
  areaMin: string;
  areaMax: string;
  features: string[];
}

interface PropertyFilterProps {
  onFilter: (filters: FilterState) => void;
  lang?: LangCode;
}

const LOCATION_SUGGESTIONS = [
  'Alicante Centro', 'Playa San Juan', 'Hurchillo', 'Vega Baja',
  'Torrevieja', 'Elche', 'Santa Pola', 'Guardamar del Segura',
  'Orihuela Costa', 'Benidorm',
];

const ROOM_OPTIONS = ['1', '2', '3', '4+'];

// Traducciones internas del filtro
const filterTranslations: Record<string, Record<string, string>> = {
  es: {
    title: 'Buscar Propiedades', less: 'Menos filtros', more: 'Más filtros',
    op_label: 'Tipo de operación', op_all: 'Todos', op_venta: 'Venta', op_alquiler: 'Alquiler',
    op_parcelas: 'Parcelas / Lotes / Fincas', op_construccion: 'En Construcción',
    location_label: 'Ubicación', location_placeholder: 'Ciudad, barrio o código postal...',
    price_label: 'Rango de precio (€)', price_min: 'Mínimo', price_max: 'Máximo',
    type_label: 'Tipo de propiedad',
    types: ['Casa', 'Apartamento', 'Local comercial', 'Terreno', 'Otros'],
    rooms_label: 'Habitaciones', baths_label: 'Baños', any: 'Cualquiera',
    area_label: 'Superficie (m²)', area_min: 'Mín m²', area_max: 'Máx m²',
    features_label: 'Características adicionales',
    features: ['Piscina', 'Garaje', 'Jardín', 'Amueblado', 'Terraza'],
    reset: 'Limpiar filtros', search: 'Buscar propiedades',
  },
  en: {
    title: 'Search Properties', less: 'Fewer filters', more: 'More filters',
    op_label: 'Operation type', op_all: 'All', op_venta: 'For Sale', op_alquiler: 'For Rent',
    op_parcelas: 'Plots / Lots / Farms', op_construccion: 'Under Construction',
    location_label: 'Location', location_placeholder: 'City, neighborhood or postal code...',
    price_label: 'Price range (€)', price_min: 'Minimum', price_max: 'Maximum',
    type_label: 'Property type',
    types: ['House', 'Apartment', 'Commercial space', 'Land', 'Others'],
    rooms_label: 'Bedrooms', baths_label: 'Bathrooms', any: 'Any',
    area_label: 'Area (m²)', area_min: 'Min m²', area_max: 'Max m²',
    features_label: 'Additional features',
    features: ['Pool', 'Garage', 'Garden', 'Furnished', 'Terrace'],
    reset: 'Clear filters', search: 'Search properties',
  },
  ru: {
    title: 'Поиск объектов', less: 'Меньше фильтров', more: 'Больше фильтров',
    op_label: 'Тип операции', op_all: 'Все', op_venta: 'Продажа', op_alquiler: 'Аренда',
    op_parcelas: 'Участки / Лоты / Фермы', op_construccion: 'Строящиеся',
    location_label: 'Расположение', location_placeholder: 'Город, район или почтовый индекс...',
    price_label: 'Диапазон цен (€)', price_min: 'Минимум', price_max: 'Максимум',
    type_label: 'Тип недвижимости',
    types: ['Дом', 'Квартира', 'Коммерческое помещение', 'Земля', 'Другое'],
    rooms_label: 'Спальни', baths_label: 'Ванные', any: 'Любое',
    area_label: 'Площадь (м²)', area_min: 'Мин м²', area_max: 'Макс м²',
    features_label: 'Дополнительные характеристики',
    features: ['Бассейн', 'Гараж', 'Сад', 'Меблированная', 'Терраса'],
    reset: 'Сбросить фильтры', search: 'Найти объекты',
  },
  pl: {
    title: 'Szukaj nieruchomości', less: 'Mniej filtrów', more: 'Więcej filtrów',
    op_label: 'Typ operacji', op_all: 'Wszystkie', op_venta: 'Sprzedaż', op_alquiler: 'Wynajem',
    op_parcelas: 'Działki / Parcele / Farmy', op_construccion: 'W budowie',
    location_label: 'Lokalizacja', location_placeholder: 'Miasto, dzielnica lub kod pocztowy...',
    price_label: 'Zakres cen (€)', price_min: 'Minimum', price_max: 'Maksimum',
    type_label: 'Typ nieruchomości',
    types: ['Dom', 'Apartament', 'Lokal użytkowy', 'Grunt', 'Inne'],
    rooms_label: 'Sypialnie', baths_label: 'Łazienki', any: 'Dowolna',
    area_label: 'Powierzchnia (m²)', area_min: 'Min m²', area_max: 'Maks m²',
    features_label: 'Dodatkowe cechy',
    features: ['Basen', 'Garaż', 'Ogród', 'Umeblowane', 'Taras'],
    reset: 'Wyczyść filtry', search: 'Szukaj nieruchomości',
  },
  fr: {
    title: 'Rechercher des propriétés', less: 'Moins de filtres', more: 'Plus de filtres',
    op_label: 'Type d\'opération', op_all: 'Tous', op_venta: 'Vente', op_alquiler: 'Location',
    op_parcelas: 'Terrains / Lots / Fermes', op_construccion: 'En construction',
    location_label: 'Localisation', location_placeholder: 'Ville, quartier ou code postal...',
    price_label: 'Fourchette de prix (€)', price_min: 'Minimum', price_max: 'Maximum',
    type_label: 'Type de propriété',
    types: ['Maison', 'Appartement', 'Local commercial', 'Terrain', 'Autres'],
    rooms_label: 'Chambres', baths_label: 'Salles de bain', any: 'Peu importe',
    area_label: 'Surface (m²)', area_min: 'Min m²', area_max: 'Max m²',
    features_label: 'Caractéristiques supplémentaires',
    features: ['Piscine', 'Garage', 'Jardin', 'Meublé', 'Terrasse'],
    reset: 'Effacer les filtres', search: 'Rechercher des propriétés',
  },
  de: {
    title: 'Immobilien suchen', less: 'Weniger Filter', more: 'Mehr Filter',
    op_label: 'Operationstyp', op_all: 'Alle', op_venta: 'Kauf', op_alquiler: 'Miete',
    op_parcelas: 'Grundstücke / Parzellen / Höfe', op_construccion: 'Im Bau',
    location_label: 'Standort', location_placeholder: 'Stadt, Stadtteil oder Postleitzahl...',
    price_label: 'Preisbereich (€)', price_min: 'Minimum', price_max: 'Maximum',
    type_label: 'Immobilientyp',
    types: ['Haus', 'Wohnung', 'Gewerberaum', 'Grundstück', 'Sonstige'],
    rooms_label: 'Schlafzimmer', baths_label: 'Badezimmer', any: 'Beliebig',
    area_label: 'Fläche (m²)', area_min: 'Min m²', area_max: 'Max m²',
    features_label: 'Zusätzliche Merkmale',
    features: ['Pool', 'Garage', 'Garten', 'Möbliert', 'Terrasse'],
    reset: 'Filter zurücksetzen', search: 'Immobilien suchen',
  },
  pt: {
    title: 'Pesquisar propriedades', less: 'Menos filtros', more: 'Mais filtros',
    op_label: 'Tipo de operação', op_all: 'Todos', op_venta: 'Venda', op_alquiler: 'Arrendamento',
    op_parcelas: 'Terrenos / Lotes / Quintas', op_construccion: 'Em construção',
    location_label: 'Localização', location_placeholder: 'Cidade, bairro ou código postal...',
    price_label: 'Intervalo de preço (€)', price_min: 'Mínimo', price_max: 'Máximo',
    type_label: 'Tipo de propriedade',
    types: ['Casa', 'Apartamento', 'Espaço comercial', 'Terreno', 'Outros'],
    rooms_label: 'Quartos', baths_label: 'Casas de banho', any: 'Qualquer',
    area_label: 'Área (m²)', area_min: 'Mín m²', area_max: 'Máx m²',
    features_label: 'Características adicionais',
    features: ['Piscina', 'Garagem', 'Jardim', 'Mobilado', 'Terraço'],
    reset: 'Limpar filtros', search: 'Pesquisar propriedades',
  },
  it: {
    title: 'Cerca proprietà', less: 'Meno filtri', more: 'Più filtri',
    op_label: 'Tipo di operazione', op_all: 'Tutti', op_venta: 'Vendita', op_alquiler: 'Affitto',
    op_parcelas: 'Terreni / Lotti / Fattorie', op_construccion: 'In costruzione',
    location_label: 'Posizione', location_placeholder: 'Città, quartiere o codice postale...',
    price_label: 'Fascia di prezzo (€)', price_min: 'Minimo', price_max: 'Massimo',
    type_label: 'Tipo di proprietà',
    types: ['Casa', 'Appartamento', 'Locale commerciale', 'Terreno', 'Altri'],
    rooms_label: 'Camere da letto', baths_label: 'Bagni', any: 'Qualsiasi',
    area_label: 'Superficie (m²)', area_min: 'Min m²', area_max: 'Max m²',
    features_label: 'Caratteristiche aggiuntive',
    features: ['Piscina', 'Garage', 'Giardino', 'Arredato', 'Terrazza'],
    reset: 'Cancella filtri', search: 'Cerca proprietà',
  },
};

export default function PropertyFilter({ onFilter, lang = 'es' }: PropertyFilterProps) {
  const ft = (key: string) => filterTranslations[lang]?.[key] ?? filterTranslations['es'][key] ?? key;
  const PROPERTY_TYPES = filterTranslations[lang]?.types ?? filterTranslations['es'].types;
  const FEATURES = filterTranslations[lang]?.features ?? filterTranslations['es'].features;

  const [filters, setFilters] = useState<FilterState>({
    operationType: 'all',
    propertyTypes: [],
    location: '',
    priceMin: '',
    priceMax: '',
    bedrooms: '',
    bathrooms: '',
    areaMin: '',
    areaMax: '',
    features: [],
  });

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleLocationChange = (value: string) => {
    setFilters(prev => ({ ...prev, location: value }));
    if (value.length > 0) {
      const matches = LOCATION_SUGGESTIONS.filter(s =>
        s.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const togglePropertyType = (type: string) => {
    setFilters(prev => ({
      ...prev,
      propertyTypes: prev.propertyTypes.includes(type)
        ? prev.propertyTypes.filter(t => t !== type)
        : [...prev.propertyTypes, type],
    }));
  };

  const toggleFeature = (feature: string) => {
    setFilters(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const handleSearch = () => { onFilter(filters); };

  const handleReset = () => {
    const reset: FilterState = {
      operationType: 'all', propertyTypes: [], location: '',
      priceMin: '', priceMax: '', bedrooms: '', bathrooms: '',
      areaMin: '', areaMax: '', features: [],
    };
    setFilters(reset);
    onFilter(reset);
  };

  const featureIcons: Record<number, string> = {
    0: 'ri-water-flash-line', 1: 'ri-car-line',
    2: 'ri-plant-line', 3: 'ri-sofa-line', 4: 'ri-sun-line',
  };

  return (
    <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden mb-8 sm:mb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-red-900 px-5 sm:px-8 py-4 sm:py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-filter-3-line text-white text-lg sm:text-xl"></i>
          </div>
          <h3 className="text-white font-semibold text-base sm:text-lg">{ft('title')}</h3>
        </div>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-white/80 hover:text-white text-xs sm:text-sm flex items-center gap-1 sm:gap-2 transition-colors cursor-pointer whitespace-nowrap"
        >
          {showAdvanced ? ft('less') : ft('more')}
          <i className={`ri-arrow-${showAdvanced ? 'up' : 'down'}-s-line`}></i>
        </button>
      </div>

      <div className="p-4 sm:p-8">
        {/* Row 1: Operation Type */}
        <div className="mb-5 sm:mb-6">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            {ft('op_label')}
          </label>
          <div className="flex flex-wrap bg-gray-100 rounded-xl p-1 gap-1">
            {([
              { value: 'all', labelKey: 'op_all' },
              { value: 'venta', labelKey: 'op_venta' },
              { value: 'alquiler', labelKey: 'op_alquiler' },
              { value: 'parcelas', labelKey: 'op_parcelas' },
              { value: 'construccion', labelKey: 'op_construccion' },
            ] as const).map(op => (
              <button
                key={op.value}
                onClick={() => setFilters(prev => ({ ...prev, operationType: op.value }))}
                className={`flex-1 min-w-fit py-2.5 px-3 rounded-lg text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  filters.operationType === op.value
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {ft(op.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: Location */}
        <div className="mb-5 sm:mb-6 relative">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            {ft('location_label')}
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-line text-red-500 text-base"></i>
            </div>
            <input
              type="text"
              value={filters.location}
              onChange={e => handleLocationChange(e.target.value)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              onFocus={() => filters.location && setShowSuggestions(filteredSuggestions.length > 0)}
              placeholder={ft('location_placeholder')}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
            />
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden">
                {filteredSuggestions.map(s => (
                  <button
                    key={s}
                    onMouseDown={() => {
                      setFilters(prev => ({ ...prev, location: s }));
                      setShowSuggestions(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-map-pin-2-line text-red-400 text-xs"></i>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Row 3: Price Range */}
        <div className="mb-5 sm:mb-6">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            {ft('price_label')}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={filters.priceMin}
              onChange={e => setFilters(prev => ({ ...prev, priceMin: e.target.value }))}
              placeholder={ft('price_min')}
              className="w-full px-3 sm:px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
            />
            <span className="text-gray-400 font-medium text-sm whitespace-nowrap">—</span>
            <input
              type="number"
              value={filters.priceMax}
              onChange={e => setFilters(prev => ({ ...prev, priceMax: e.target.value }))}
              placeholder={ft('price_max')}
              className="w-full px-3 sm:px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
            />
          </div>
        </div>

        {/* Row 4: Property Type */}
        <div className="mb-5 sm:mb-6">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            {ft('type_label')}
          </label>
          <div className="flex flex-wrap gap-2">
            {PROPERTY_TYPES.map((type: string) => (
              <button
                key={type}
                onClick={() => togglePropertyType(type)}
                className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                  filters.propertyTypes.includes(type)
                    ? 'bg-red-600 text-white border-red-600 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-red-300 hover:text-red-600'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Row 5: Bedrooms + Bathrooms */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 mb-5 sm:mb-6">
          {[
            { labelKey: 'rooms_label', icon: 'ri-hotel-bed-line', key: 'bedrooms' as const },
            { labelKey: 'baths_label', icon: 'ri-drop-line', key: 'bathrooms' as const },
          ].map(({ labelKey, icon, key }) => (
            <div key={key}>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                <i className={`${icon} mr-1`}></i> {ft(labelKey)}
              </label>
              <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, [key]: '' }))}
                  className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                    filters[key] === ''
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'
                  }`}
                >
                  {ft('any')}
                </button>
                {ROOM_OPTIONS.map(n => (
                  <button
                    key={n}
                    onClick={() => setFilters(prev => ({ ...prev, [key]: n }))}
                    className={`flex-1 min-w-[2.5rem] py-2 rounded-xl text-xs sm:text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      filters[key] === n
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="border-t border-gray-100 pt-5 sm:pt-6 mb-5 sm:mb-6 space-y-5 sm:space-y-6">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                <i className="ri-ruler-line mr-1"></i> {ft('area_label')}
              </label>
              <div className="flex items-center gap-3 max-w-sm">
                <input
                  type="number"
                  value={filters.areaMin}
                  onChange={e => setFilters(prev => ({ ...prev, areaMin: e.target.value }))}
                  placeholder={ft('area_min')}
                  className="w-full px-3 sm:px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
                />
                <span className="text-gray-400 font-medium whitespace-nowrap">—</span>
                <input
                  type="number"
                  value={filters.areaMax}
                  onChange={e => setFilters(prev => ({ ...prev, areaMax: e.target.value }))}
                  placeholder={ft('area_max')}
                  className="w-full px-3 sm:px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {ft('features_label')}
              </label>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {FEATURES.map((feature: string, idx: number) => (
                  <button
                    key={feature}
                    onClick={() => toggleFeature(feature)}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      filters.features.includes(feature)
                        ? 'bg-red-600 text-white border-red-600 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-red-300 hover:text-red-600'
                    }`}
                  >
                    <i className={`${featureIcons[idx] ?? 'ri-star-line'} text-sm sm:text-base`}></i>
                    {feature}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 pt-2">
          <button
            onClick={handleReset}
            className="flex items-center justify-center gap-2 text-gray-500 hover:text-gray-800 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap py-2"
          >
            <i className="ri-refresh-line"></i>
            {ft('reset')}
          </button>

          <button
            onClick={handleSearch}
            className="bg-red-600 hover:bg-red-700 text-white px-8 sm:px-10 py-3.5 rounded-full text-sm sm:text-base font-semibold transition-all shadow-lg hover:shadow-red-200 flex items-center justify-center gap-3 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line text-base sm:text-lg"></i>
            {ft('search')}
          </button>
        </div>
      </div>
    </div>
  );
}
